import { A } from '@adornis/base/env-info.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import '@adornis/buchungsheld-components/authentication/bh-login.js';
import { subscribeGlobalNotification } from '@adornis/buchungsheld-db/api/global-notification.js';
import { handleNesletterOptInReuest } from '@adornis/buchungsheld-db/api/users.js';
import { Product } from '@adornis/buchungsheld-db/entities/products.js';
import { Permission } from '@adornis/buchungsheld-db/enums/roles.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-section.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import '@adornis/fonts/fonts.js';
import { initApp } from '@adornis/functional-lit/init-app.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { webcomponent } from '@adornis/functional-lit/webcomponent.js';
import { makeRouter, type RouteConfig } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingStateSignal } from '@adornis/router/client/routing-state-signal.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { authenticationGuard } from '@adornis/user-forms/authentication-guard.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { html, nothing } from 'lit';
import { filter } from 'rxjs';
import './components/bh-footer.js';
import './components/bh-topbar.js';
import './pages/admin/bh-page-admin.js';
import { adminRouter, BhAdminDashboard } from './pages/admin/bh-page-admin.js';
import './pages/bh-page-accessibility.js';
import './pages/bh-page-action.js';
import './pages/bh-page-confirm-mail.js';
import './pages/bh-page-imprint.js';
import './pages/bh-page-landing.js';
import './pages/bh-page-participated.js';
import './pages/bh-page-privacy.js';
import './pages/bh-page-product-participated.js';
import './pages/bh-page-profile.js';
import './pages/bh-page-reset-password.js';
import './pages/bh-page-terms.js';
import './pages/debug/bh-page-debug.js';
import './pages/registration/bh-page-booked.js';
import './pages/registration/bh-page-registration.js';
import { RegistrationProductDetails } from './pages/registration/functional-components/RegistrationProductDetails.js';

export const mainRouter = makeRouter(
  {
    root: {
      path: '/',
      render: () => html` <bh-page-landing></bh-page-landing> `,
    },
    action: {
      path: '/action',
      render: () => html` <bh-page-action></bh-page-action> `,
    },
    ['reset-password']: {
      path: '/reset-password',
      render: () => html` <bh-page-reset-password></bh-page-reset-password> `,
    },
    ['confirmation-mail']: {
      path: '/confirm-mail',
      render: () => html` <bh-page-confirm-mail></bh-page-confirm-mail> `,
    },
    registration: {
      path: '/registration',
      render: () => simplePage('registration', html` <bh-page-registration mode="url"></bh-page-registration> `),
    },
    booked: {
      path: '/booked',
      render: () => simplePage('Buchungs Übsersicht', html` <bh-page-booked mode="url"></bh-page-booked> `),
    },
    productDetail: {
      path: '/product/:id',
      render: params =>
        stateful(() => {
          const productID = params.id;
          const product = useObservable(
            () => subscribeByID(Product, productID)({ _id: 1, institutionID: 1 }),
            [productID],
          );
          const institutionID = product?.institutionID;
          if (!institutionID) return html`<x-loading-state></x-loading-state>`;
          const productOverviewLink = '/registration/?step=workshop&institution=' + institutionID;
          const bookLink = A.absoluteUrl(
            '/registration?step=personal&institution=' +
              institutionID +
              '&data=%7B%22_class%22%3A%22RegistrationData%22%2C%22institutionID%22%3A%22testing_institution_2%22%2C%22productData%22%3A%5B%7B%22_class%22%3A%22RegistrationWorkshopData%22%2C%22_id%22%3A%2266fd143d20e1ae0ba7020%22%2C%22productID%22%3A%22' +
              productID +
              '%22%7D%5D%2C%22acceptedTerms%22%3Afalse%2C%22userStammdaten%22%3Anull%2C%22user%22%3Anull%7D',
          );

          const lang = useObservable(
            () =>
              globalLanguageSubject.pipe(
                filter(isDefined),
                // map(_ => clientTranslator.translate),
              ),
            [42],
          );

          return simplePage(
            'Produkt Detail',
            html`<x-flex
              ${css({
                width: '100%',
                flexDirection: 'column',
                alignItems: 'center',
              })}
            >
              <x-flex vertical ${css({ padding: '20px', maxWidth: '1200px', gap: '40px' })}>
                <x-flex ${css({ width: '100%' })} horizontal space="sm">
                  <bh-breadcrumb
                    .items=${[
                      {
                        content: lang === 'de' ? 'Produkt Übersicht' : 'Product Overview', //translate?.('REGISTRATION', 'productOverview') ?? '',
                        active: false,
                        href: productOverviewLink,
                      },
                    ]}
                  ></bh-breadcrumb>
                </x-flex>

                ${RegistrationProductDetails({ productID: params.id as string })}
                <x-button @click=${() => goTo(bookLink)}> Jetzt Buchen </x-button>
              </x-flex>
            </x-flex>`,
            { showBackButton: false },
          );
        })(),
    },
    imprint: {
      path: '/impressum',
      render: () => simplePage('Impressum', html` <bh-page-imprint></bh-page-imprint> `, { showBackButton: true }),
    },
    dataPrivacy: {
      path: '/datenschutz',
      render: () => simplePage('Datenschutz', html` <bh-page-privacy></bh-page-privacy> `, { showBackButton: true }),
    },
    terms: {
      path: '/terms',
      render: () =>
        simplePage('Nutzungsbedingungen', html` <bh-page-terms></bh-page-terms> `, { showBackButton: true }),
    },
    accessibility: {
      path: '/barrierefreiheit',
      render: () =>
        simplePage('terms', html` <bh-page-accessibility></bh-page-accessibility> `, { showBackButton: true }),
    },
    admin: {
      path: '/admin*',
      render: () => {
        const guard = permissionGuard(Permission.VIEW_ADMIN_DASHBOARD, () => BhAdminDashboard.render(), {
          forbiddenTemplate: html`
            <x-empty-placeholder .img=${'close'} .text=${'Sie haben hierfür keine Berechtigung.'}></x-empty-placeholder>
          `,
          context: ANY_CONTEXT,
        });

        return authenticationGuard(
          () => html`${guard}`,
          html` <x-flex h100 w100 center crossaxis-center flex> <bh-login></bh-login> </x-flex> `,
        );
      },
      subRouter: adminRouter,
    },
    profile: {
      path: '/profile',
      render: () =>
        simplePage(
          'profile',
          loggedInPages(html` <bh-page-profile @onGoToWorkshopOffers=${() => goTo('/')}></bh-page-profile> `),
        ),
    },
    ['participated-appointment']: {
      path: '/extern/confirm-appointment-participation',
      render: () => loggedInPages(html` <bh-page-participated></bh-page-participated> `),
    },
    ['participated-product']: {
      path: '/extern/confirm-product-participation',
      render: () => loggedInPages(html` <bh-page-product-participated></bh-page-product-participated> `),
    },
    ['newsletter-optin']: {
      path: '/i-want-receive-newsletter',
      render: () => handleNewsletterOptIn(),
    },
    ...(baseConfig.get('DEPLOYMENT') === 'development'
      ? ({
          debug: {
            path: '/debug',
            render: () => html` <bh-page-debug></bh-page-debug> `,
          },
          debug2: {
            path: '/debug2',
            render: () => html` <x-data-view-column-selection></x-data-view-column-selection> `,
          },
        } as Record<string, RouteConfig<any>>)
      : {}),
  },
  {
    routerName: 'main-router',
    fallback: () => html` <x-empty-placeholder .img=${'close'} .text=${'404 page not found'}> </x-empty-placeholder> `,
  },
);

mainRouter.init();

initApp(
  webcomponent('main-element', () => mainRouter.outlet.value, {
    shadowRoot: true,
    connectedCallback() {
      subscribeGlobalNotification()().subscribe(message => {
        XSnackbar.show(message, 5000);
      });
    },
    styles: {
      ':host': {
        overflow: 'auto',
      },
    },
  }),
);

function simplePage(page: string, content, options: { showBackButton?: boolean } = {}) {
  const backButton = html` <x-section>
    <x-flex
      space="sm"
      horizontal
      ${css({ alignContent: 'center', justifyContent: 'start', padding: '20px 0', cursor: 'pointer' })}
      @click=${() => window.history.back()}
    >
      <x-icon>arrow_back</x-icon>
      <x-button mode="text">zurück</x-button>
    </x-flex>
  </x-section>`;
  return html`
    <x-flex ${css({ maxHeight: '100%', overflow: 'auto' })}>
      <bh-topbar page=${page}></bh-topbar>
      ${options?.showBackButton ? backButton : nothing}
      <!-- -->
      ${content}
      <bh-footer></bh-footer>
    </x-flex>
  `;
}

const handleNewsletterOptIn = stateful(() => {
  const searchParams = routingStateSignal.value.searchParams;
  const userID = searchParams.get('userID')!;
  const token = searchParams.get('token')!;
  const { value, loading } = useQuery(
    () => (userID && token ? handleNesletterOptInReuest(userID, token)() : new Promise(() => {})),
    [userID, token],
  );
  if (loading) return html`<tr-loading-state></tr-loader-state>`;
  if (!value) return html`<x-empty-placeholder .img=${'error'} .text=${'Link is ungültig'}> </x-empty-placeholder>`;

  XSnackbar.show('Newsletter abonniert');
  if (value && !loading) {
    goTo('/profile');
    // TODO: figure out why loginpage is not shown when user is not togged in
    setTimeout(() => window.location.reload(), 2000);
  }
  return nothing;
});

function loggedInPages(content: Renderable) {
  return authenticationGuard(
    () => content,
    html`
      <x-flex
        h100
        w100
        center
        crossaxis-center
        flex
        ${css({ maxHeight: '700px' /* needed for iframe embed method */, minHeight: '500px', height: '100%' })}
      >
        <bh-login></bh-login>
      </x-flex>
    `,
  );
}
